import React, { useEffect, useState } from 'react';
import { ExternalLink, FileText } from 'lucide-react';
import organizationsService from '../services/organizationsService';

/**
 * Past invoices for an organisation.
 *
 * The Billing tab could show what the organisation pays now and hand over to
 * the Stripe portal, but not what it has already been charged — which is the
 * one billing question somebody usually opens the page to answer, and the one
 * their finance team asks them.
 *
 * Amounts are rendered from the minor units and currency Stripe reports rather
 * than assumed to be euros: an organisation billed in USD being shown a € sign
 * is a support ticket, and a silent one, because the number looks right.
 */
function amount(cents, currency) {
  if (typeof cents !== 'number') return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'eur').toUpperCase(),
    }).format(cents / 100);
  } catch {
    // An unknown currency code must not blank the whole row.
    return `${(cents / 100).toFixed(2)} ${(currency || '').toUpperCase()}`;
  }
}

export default function OrganizationInvoices({ orgId }) {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next = await organizationsService.getInvoices(orgId);
      if (!cancelled) setRows(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  if (rows === null) return <p className="org-hint">Loading invoices…</p>;

  if (rows.length === 0) {
    return <p className="org-hint">No invoices yet.</p>;
  }

  return (
    <div className="org-invoices">
      <h4 className="org-pending-title">Invoices</h4>

      {rows.map((inv) => (
        <div key={inv.id} className="org-invoice-row">
          <FileText size={14} aria-hidden className="org-pending-icon" />
          <span className="org-invoice-date">
            {inv.created ? new Date(inv.created * 1000).toLocaleDateString('en-GB') : '—'}
          </span>
          <span className="org-invoice-amount">{amount(inv.amount_paid, inv.currency)}</span>
          <span className={`org-invoice-status org-invoice-status--${inv.status}`}>
            {inv.status}
          </span>
          {inv.hosted_invoice_url && (
            <a
              className="org-icon-btn"
              href={inv.hosted_invoice_url}
              target="_blank"
              rel="noreferrer noopener"
              title="View invoice"
              aria-label="View this invoice"
            >
              <ExternalLink size={13} aria-hidden />
            </a>
          )}
        </div>
      ))}
    </div>
  );
}
